/**
 * Usage Control Service
 * Tracks and manages tenant resource usage including:
 * - Storage (attachments, files)
 * - Entity counts (customers, leads, opportunities, etc.)
 * - AI token usage (chat, analysis, etc.)
 */

import axios from 'axios';

class UsageControlService {
  constructor() {
    this.baseURL = '/api/usage-control';
  }

  /**
   * Get authentication headers
   */
  getHeaders() {
    return {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    };
  }

  // ==================== GLOBAL ADMIN - LIMIT MANAGEMENT ====================

  /**
   * Get global usage limits configuration
   * @returns {Promise<Object>} Global limits configuration
   */
  async getGlobalLimits() {
    try {
      const response = await axios.get(`${this.baseURL}/limits/global`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get global limits:', error);
      throw error;
    }
  }

  /**
   * Update global usage limits
   * @param {Object} limits - Limits configuration
   * @returns {Promise<Object>} Updated limits
   */
  async updateGlobalLimits(limits) {
    try {
      const response = await axios.put(`${this.baseURL}/limits/global`, limits, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update global limits:', error);
      throw error;
    }
  }

  /**
   * Get tenant-specific usage limits
   * @param {string} tenantCode - Tenant code
   * @returns {Promise<Object>} Tenant limits
   */
  async getTenantLimits(tenantCode) {
    try {
      const response = await axios.get(`${this.baseURL}/limits/tenant/${tenantCode}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant limits:', error);
      throw error;
    }
  }

  /**
   * Update tenant-specific usage limits
   * @param {string} tenantCode - Tenant code
   * @param {Object} limits - Limits configuration
   * @returns {Promise<Object>} Updated limits
   */
  async updateTenantLimits(tenantCode, limits) {
    try {
      const response = await axios.put(`${this.baseURL}/limits/tenant/${tenantCode}`, limits, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update tenant limits:', error);
      throw error;
    }
  }

  // ==================== USAGE TRACKING ====================

  /**
   * Get current usage statistics for a tenant
   * @param {string} tenantCode - Tenant code
   * @returns {Promise<Object>} Usage statistics
   */
  async getTenantUsage(tenantCode) {
    try {
      const response = await axios.get(`${this.baseURL}/usage/tenant/${tenantCode}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant usage:', error);
      throw error;
    }
  }

  /**
   * Get usage statistics for all tenants (Global Admin only)
   * @returns {Promise<Array>} Array of tenant usage statistics
   */
  async getAllTenantsUsage() {
    try {
      const response = await axios.get(`${this.baseURL}/usage/all-tenants`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get all tenants usage:', error);
      throw error;
    }
  }

  /**
   * Get detailed storage usage breakdown
   * @param {string} tenantCode - Tenant code
   * @returns {Promise<Object>} Storage usage details
   */
  async getStorageUsage(tenantCode) {
    try {
      const response = await axios.get(`${this.baseURL}/usage/storage/${tenantCode}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get storage usage:', error);
      throw error;
    }
  }

  /**
   * Get entity counts breakdown
   * @param {string} tenantCode - Tenant code
   * @returns {Promise<Object>} Entity counts
   */
  async getEntityCounts(tenantCode) {
    try {
      const response = await axios.get(`${this.baseURL}/usage/entities/${tenantCode}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get entity counts:', error);
      throw error;
    }
  }

  /**
   * Get AI token usage statistics
   * @param {string} tenantCode - Tenant code
   * @param {Object} options - Query options (startDate, endDate, groupBy)
   * @returns {Promise<Object>} AI token usage
   */
  async getAITokenUsage(tenantCode, options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      if (options.groupBy) params.append('groupBy', options.groupBy);

      const response = await axios.get(
        `${this.baseURL}/usage/ai-tokens/${tenantCode}?${params.toString()}`,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get AI token usage:', error);
      throw error;
    }
  }

  // ==================== USAGE ALERTS ====================

  /**
   * Get usage alerts for a tenant
   * @param {string} tenantCode - Tenant code
   * @returns {Promise<Array>} Array of usage alerts
   */
  async getUsageAlerts(tenantCode) {
    try {
      const response = await axios.get(`${this.baseURL}/alerts/${tenantCode}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get usage alerts:', error);
      throw error;
    }
  }

  /**
   * Configure usage alert thresholds
   * @param {string} tenantCode - Tenant code
   * @param {Object} alertConfig - Alert configuration
   * @returns {Promise<Object>} Updated alert config
   */
  async updateAlertThresholds(tenantCode, alertConfig) {
    try {
      const response = await axios.put(
        `${this.baseURL}/alerts/${tenantCode}/thresholds`,
        alertConfig,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update alert thresholds:', error);
      throw error;
    }
  }

  // ==================== USAGE HISTORY ====================

  /**
   * Get historical usage data
   * @param {string} tenantCode - Tenant code
   * @param {Object} options - Query options (startDate, endDate, metric)
   * @returns {Promise<Array>} Historical usage data
   */
  async getUsageHistory(tenantCode, options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      if (options.metric) params.append('metric', options.metric);
      if (options.interval) params.append('interval', options.interval);

      const response = await axios.get(
        `${this.baseURL}/usage/history/${tenantCode}?${params.toString()}`,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get usage history:', error);
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Calculate usage percentage
   * @param {number} current - Current usage
   * @param {number} limit - Usage limit
   * @returns {number} Percentage (0-100)
   */
  calculateUsagePercentage(current, limit) {
    if (!limit || limit === 0) return 0;
    return Math.min(Math.round((current / limit) * 100), 100);
  }

  /**
   * Get usage status (normal, warning, critical, exceeded)
   * @param {number} percentage - Usage percentage
   * @returns {string} Status
   */
  getUsageStatus(percentage) {
    if (percentage >= 100) return 'exceeded';
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning';
    return 'normal';
  }

  /**
   * Format bytes to human-readable format
   * @param {number} bytes - Bytes
   * @param {number} decimals - Decimal places
   * @returns {string} Formatted string
   */
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * Format number with thousand separators
   * @param {number} num - Number
   * @returns {string} Formatted string
   */
  formatNumber(num) {
    if (num === null || num === undefined || isNaN(num)) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Check if usage exceeds limit
   * @param {number} current - Current usage
   * @param {number} limit - Usage limit
   * @returns {boolean} True if exceeded
   */
  isLimitExceeded(current, limit) {
    if (!limit || limit === -1) return false; // -1 means unlimited
    return current >= limit;
  }

  /**
   * Get remaining capacity
   * @param {number} current - Current usage
   * @param {number} limit - Usage limit
   * @returns {number} Remaining capacity
   */
  getRemainingCapacity(current, limit) {
    if (!limit || limit === -1) return Infinity;
    return Math.max(0, limit - current);
  }

  // ==================== EXPORT/REPORTING ====================

  /**
   * Export usage report
   * @param {string} tenantCode - Tenant code
   * @param {Object} options - Export options (format, startDate, endDate)
   * @returns {Promise<Blob>} Report file
   */
  async exportUsageReport(tenantCode, options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.format) params.append('format', options.format);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);

      const response = await axios.get(
        `${this.baseURL}/reports/export/${tenantCode}?${params.toString()}`,
        {
          headers: this.getHeaders(),
          responseType: 'blob'
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to export usage report:', error);
      throw error;
    }
  }

  /**
   * Schedule automated usage reports
   * @param {string} tenantCode - Tenant code
   * @param {Object} schedule - Schedule configuration
   * @returns {Promise<Object>} Schedule confirmation
   */
  async scheduleUsageReport(tenantCode, schedule) {
    try {
      const response = await axios.post(
        `${this.baseURL}/reports/schedule/${tenantCode}`,
        schedule,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to schedule usage report:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const usageControlService = new UsageControlService();
export default usageControlService;

